// 블록 단위 플랫폼 탭.
//
// 워크스페이스 상단(셸)이 아니라 **값이 실제로 갈리는 블록의 헤더**에 붙인다.
// 상단에 하나만 두면 "이 화면 전체가 그 플랫폼으로 좁혀졌다"고 읽히는데, 같은 화면의
// 오디언스 스트립·채널 규모·채널 퍼포먼스는 3채널을 함께 보여주는 블록이라 탭과 무관하게
// 그대로 남는다. 그 어긋남이 화면을 거짓말하게 만든다(TikTok 탭인데 "합산 팔로워 9.5만").
//
// 그래서 탭은 갈리는 블록만 소유한다 — 탭이 보이는 곳이 곧 플랫폼별로 값이 바뀌는 곳이다.
import type { KeyboardEvent } from 'react'
import type { Platform } from '../creatorsApi'

const TABS: { key: Platform; label: string }[] = [
  { key: 'youtube', label: 'YouTube' },
  { key: 'instagram', label: 'Instagram' },
  { key: 'tiktok', label: 'TikTok' },
]

interface Props {
  value: Platform
  onChange: (platform: Platform) => void
  /** 스크린리더가 어느 블록의 탭인지 알 수 있게 — 한 화면에 여러 개가 뜬다. */
  label: string
  /**
   * 새 플랫폼 응답을 기다리는 중인가.
   *
   * 탭 자체는 클릭 즉시 활성 표시가 바뀌지만 **아래 값은 이전 플랫폼 것이 그대로 남는다**(응답까지 0.2~0.8초).
   * 표시가 없으면 그 사이 숫자를 새 플랫폼 값으로 읽게 된다 — 값을 가리는 대신
   * 컨트롤 옆에서 "아직 안 왔다"고 말한다.
   */
  busy?: boolean
}

export function PlatformTabs({ value, onChange, label, busy }: Props) {
  // 탭 패턴의 키보드 규약 — 좌우(위아래)로 이동, Home/End 로 양끝. 이게 없으면 Tab 키로
  // 탭 3개를 하나씩 통과해야 해서, 탭이 여러 블록에 붙은 이 화면에선 이동 비용이 계속 쌓인다.
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const i = TABS.findIndex((t) => t.key === value)
    const next =
      e.key === 'ArrowRight' || e.key === 'ArrowDown'
        ? (i + 1) % TABS.length
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
          ? (i - 1 + TABS.length) % TABS.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? TABS.length - 1
              : -1
    if (next < 0) return
    e.preventDefault()
    onChange(TABS[next].key)
  }

  return (
    <div
      className="flex items-center gap-1"
      role="tablist"
      aria-label={label}
      aria-busy={busy}
      onKeyDown={onKeyDown}
    >
      {TABS.map((t) => {
        const on = t.key === value
        return (
          <button
            key={t.key}
            role="tab"
            aria-selected={on}
            // 선택된 탭만 탭 순서에 남는다(roving tabindex) — 탭 그룹 하나가 Tab 키 한 번을 쓴다.
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(t.key)}
            className={[
              'border px-2.5 py-1 font-text text-[11px] font-medium transition-colors',
              on
                ? 'border-brand-blue bg-brand-blue text-fg-inverse'
                : 'border-line-soft bg-surface-base text-fg-3 hover:text-fg-1',
            ].join(' ')}
          >
            {t.label}
          </button>
        )
      })}
      {busy && (
        <span className="ml-1.5 inline-flex items-center gap-1.5 font-text text-[11px] text-fg-3">
          <span
            className="h-1.5 w-1.5 flex-none animate-pulse rounded-r-pill bg-brand-blue"
            aria-hidden
          />
          불러오는 중
        </span>
      )}
    </div>
  )
}
